/**
 * Load a month of Energy Insights from the account API.
 *
 * Contracts:
 *   - Race safety: monotonic sequence counter drops stale responses so
 *     rapid month switching cannot land on an older payload.
 *   - `loading` stays true during a month change so the caller can
 *     render a skeleton without flashing the empty state.
 *   - `error` never wipes the previous month's data — the caller can
 *     show a toast / retry with the last successful month still on
 *     screen.
 *
 * Auth failures propagate as `{ code: 'UNAUTHORIZED' }` for the
 * caller; the auth interceptor in authClient.js has already tried a
 * refresh + logout.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getEnergyInsights } from '../api/energyInsightsApi';

const EMPTY_SUMMARY = Object.freeze({
  energyKWh: 0,
  sessionCount: 0,
  durationMinutes: 0,
  totalCost: 0,
  averageEnergyKWh: 0,
});

export function useEnergyInsights({ month: initialMonth = null } = {}) {
  const [month, setMonthState] = useState(initialMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const seqRef = useRef(0);

  const load = useCallback(
    async (target) => {
      const requestSeq = ++seqRef.current;
      setLoading(true);
      setError(null);

      const result = await getEnergyInsights(
        target ? { month: target } : undefined,
      );

      if (requestSeq !== seqRef.current) return;

      if (result.ok) {
        setData({
          month: result.month,
          timezone: result.timezone,
          range: result.range,
          summary: result.summary || EMPTY_SUMMARY,
          daily: result.daily || [],
          recentSessions: result.recentSessions || [],
        });
        // Adopt the resolved month (backend may have picked "current"
        // when the caller passed nothing) so the UI toggles line up.
        if (result.month) setMonthState(result.month);
        setLoading(false);
      } else {
        setError({
          code: result.error,
          message: result.message,
          status: result.status,
        });
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    load(initialMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setMonth = useCallback(
    (next) => {
      if (next === month && !error) return;
      setMonthState(next);
      load(next);
    },
    [month, error, load],
  );

  const refresh = useCallback(() => load(month), [load, month]);

  return { month, data, loading, error, setMonth, refresh };
}
